import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import { useLocation, useParams } from 'react-router-dom';
import { useRef, useState } from 'react';

import AssignmentBuilderBottomControls from './layout/AssignmentBuilderBottomControls';
import AssignmentBuilderHeader from './layout/AssignmentBuilderHeader';
import AssignmentBuilderPreview from './AssignmentBuilderPreview';
import type { AssignmentFormData } from '../types/assignmentTypes';
import AudienceLimitsModal from './overlay/AudienceLimitsModal';
import CreateNewInstanceModal from './overlay/CreateNewInstanceModal';
import EditPointInTimeWorkflowModal from './overlay/EditPointInTimeWorkflowModal';
import NewAssignmentInstanceModal from './overlay/NewAssignmentInstanceModal';
import NewAssignmentModal from './overlay/NewAssignmentModal';
import { useAdminAssignmentsStore } from '../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useUnmount } from 'usehooks-ts';

export default function EditAssignmentForm() {
  const { id } = useParams();
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useUnmount(() => {
    localStorage.removeItem('learn-app-assignment-content-selection');
  });

  const { steps, stepIndex, activeForm, audienceExceedsLimitModalIsOpen, audience500ModalIsOpen } =
    useAdminAssignmentsStore();
  const { watch } = useFormContext<AssignmentFormData>();
  const [createInstanceModalIsOpen, setCreateInstanceModalIsOpen] = useState(false);

  const isInstanceCreation = useLocation().pathname.includes('instance');

  const { simpleType } = watch();

  const renderFormBlocker =
    id && simpleType === 'pointInTime' && (activeForm === 'audience' || activeForm === 'content');

  return (
    <>
      <Flex
        position="relative"
        direction="column"
        backgroundColor="neutral.100"
        height="calc(100vh - 50px)"
        minHeight="calc(100vh - 50px)"
        maxHeight="calc(100vh - 50px)"
        backgroundImage="url('/public/images/dot-grid.png')"
        backgroundSize="cover"
        backgroundRepeat="repeat"
        backgroundPosition="center"
        overflow="hidden"
      >
        {renderFormBlocker && (
          <Box
            position="absolute"
            left={0}
            bottom={73}
            top={0}
            width="full"
            backgroundColor="whiteAlpha.700"
            zIndex={999}
          />
        )}

        <Grid
          templateColumns="repeat(12, 1fr)"
          gap={0}
          flex={1}
          overflow="hidden"
          minWidth={{ base: 'full', '3xl': '1600px' }}
          maxWidth={{ base: 'full', '3xl': '1600px' }}
          margin={{ base: '0', '3xl': 'auto' }}
          height="full"
        >
          <GridItem
            position="relative"
            id="previewGridItem"
            colSpan={7}
            overflowY="auto"
            marginBottom={16}
            height="full"
            paddingY={6}
            paddingX={28}
            ref={previewContainerRef}
            sx={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'neutral.200',
                borderRadius: '24px',
              },
            }}
          >
            <AssignmentBuilderHeader />

            <AssignmentBuilderPreview containerRef={previewContainerRef} />
          </GridItem>

          <GridItem
            position="relative"
            colSpan={5}
            backgroundColor="white"
            borderRadius="16px"
            overflowY="hidden"
            marginY={6}
            marginRight={6}
            height="calc(100% - 48px)"
          >
            {steps[stepIndex]?.formComponent}
          </GridItem>
        </Grid>

        <AssignmentBuilderBottomControls />
      </Flex>

      <NewAssignmentModal />

      <EditPointInTimeWorkflowModal />

      <NewAssignmentInstanceModal
        openCreateInstanceModal={() => setCreateInstanceModalIsOpen(true)}
      />

      {isInstanceCreation && (
        <CreateNewInstanceModal
          isOpen={createInstanceModalIsOpen}
          onClose={() => setCreateInstanceModalIsOpen(false)}
        />
      )}

      {(audience500ModalIsOpen || audienceExceedsLimitModalIsOpen) && <AudienceLimitsModal />}
    </>
  );
}
